import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BlogBreadcrumb } from "@/components/seo/BlogBreadcrumb";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";
import { Button } from "@/components/ui/button";
import { CalendarDays } from "lucide-react";
import heroImage from "@/assets/gallery/aerial-river-cabanas.jpg";

const SLUG = "best-stretch-guadalupe-river-new-braunfels";
const CANONICAL = `/blog/${SLUG}`;
const TITLE = "Best Stretch of the Guadalupe River | Son's Guadalupe";
const DESCRIPTION =
  "Son's Guadalupe sits on the best-flowing stretch of the Guadalupe River near New Braunfels — great floats even at low release rates, plus riverfront cabanas.";
const PUBLISH_DATE = "June 9, 2026";
const HERO_ALT = "Tubers floating the Guadalupe River at Son's Guadalupe near New Braunfels.";

const schemaJson = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
        {
          "@type": "ListItem",
          position: 3,
          name: "Best Stretch of the Guadalupe River",
          item: `https://sonsguadalupe.com${CANONICAL}`,
        },
      ],
    },
    {
      "@type": "BlogPosting",
      headline:
        "Why Son's Guadalupe Sits on the Best Stretch of the Guadalupe River Near New Braunfels",
      description: DESCRIPTION,
      image: `https://sonsguadalupe.com${heroImage}`,
      author: { "@type": "Organization", name: "Son's Guadalupe" },
      datePublished: "2026-06-09",
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://sonsguadalupe.com${CANONICAL}` },
      keywords:
        "best stretch Guadalupe River, Guadalupe River New Braunfels, riverfront cabanas, Canyon Lake release, family tubing",
    },
  ],
};

const BlogBestStretchGuadalupe = () => {
  const related = getRelatedPosts(SLUG, 3);

  return (
    <div className="min-h-screen">
      <SEOHead
        title={TITLE}
        description={DESCRIPTION}
        canonical={CANONICAL}
        ogImage={heroImage}
        schemaJson={schemaJson}
      />
      <Header />

      <main>
        <article className="container-narrow mx-auto px-4 md:px-8 pt-28 pb-16">
          <BlogBreadcrumb title="Best Stretch of the Guadalupe River" />

          <header className="mb-8">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-charcoal mb-4">
              Why Son's Guadalupe Sits on the Best Stretch of the Guadalupe River Near New Braunfels
            </h1>
            <p className="flex items-center gap-2 text-sm text-charcoal/60">
              <CalendarDays className="w-4 h-4" /> Published {PUBLISH_DATE}
            </p>
          </header>

          <img
            src={heroImage}
            alt={HERO_ALT}
            className="w-full h-auto rounded-2xl shadow-soft mb-10"
          />

          <div className="prose prose-lg max-w-none text-charcoal/80">
            <p className="text-xl leading-relaxed">
              If you've ever tubed the Guadalupe and walked away thinking, <em>"that was magic"</em>,
              there's a good chance you floated the stretch we sit on. Son's Guadalupe is tucked onto
              what we honestly believe is the best stretch of the Guadalupe River near New Braunfels —
              the kind of water that always seems to float well and always feels like the Texas Hill
              Country at its absolute best. Cypress trees leaning over the bank, clear green water,
              and that easy, drifting rhythm that makes a Texas summer day feel like it lasts forever.
            </p>

            <h2 className="font-heading text-2xl font-bold text-charcoal mt-10 mb-4">
              The flow advantage — why this stretch floats when others don't
            </h2>
            <p>
              Here's the part most first-time guests don't realize until they see it for themselves:
              not every part of the Guadalupe floats the same. The river is fed by releases out of
              Canyon Lake, and when those release rates drop — which they do every summer at some
              point — a lot of upstream sections get thin, slow, and scrapey. Tubes drag bottom.
              Plans get cut short. People end up walking more than floating.
            </p>
            <p>
              Our stretch is different. The run from our typical put-in at the Gruene River Resort
              down to Son's Guadalupe holds water beautifully, even on low-release days when other
              parts of the river are struggling. The channel is shaped right, the pools are deep
              enough, and the current keeps moving you along. Guests get a true river experience
              here on days when other outfitters are politely telling people to come back next week.
              It's the single biggest reason we landed where we did, and it's the reason a float
              with us tends to live up to the pictures.
            </p>

            <h2 className="font-heading text-2xl font-bold text-charcoal mt-10 mb-4">
              Right next door to the Lone Star Float House
            </h2>
            <p>
              One of the small joys of being where we are: we sit right next door to the{" "}
              <a
                href="https://lonestarfloathouse.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal underline"
              >
                Lone Star Float House
              </a>
              . It's a great spot to grab a hamburger and a cold drink and sit outside looking out
              over the Guadalupe River. A lot of our guests don't even bother going anywhere else
              for lunch — they walk next door, pick up burgers, and bring them back to eat at their
              own cabana at Son's. Easy, local, and exactly the kind of low-key meal that fits a
              river day.
            </p>

            <h2 className="font-heading text-2xl font-bold text-charcoal mt-10 mb-4">
              Gypsy River Resort nearby for RV travelers
            </h2>
            <p>
              If you're rolling into the Hill Country in an RV, our neighbors at{" "}
              <a
                href="https://gypsyriverresort.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal underline"
              >
                Gypsy River Resort
              </a>{" "}
              are close by and offer RV spots right on the river. It's a nice option for folks who
              want to bring their rig to the Guadalupe and still be a quick hop from everything
              happening on this stretch — including a day of tubing or a cabana hangout with us.
            </p>

            <h2 className="font-heading text-2xl font-bold text-charcoal mt-10 mb-4">
              A family-friendly spot on the river
            </h2>
            <p>
              We've always thought of Son's Guadalupe as a family-friendly riverfront spot — private
              cabanas, picnic tables, easy river access, and a daily guest cap so it never feels
              like a free-for-all. That said, we want to be straight with you: River Road can get a
              little wild on big weekends and holidays. If your priority is the most family-friendly
              experience possible, we'd quietly steer you toward <strong>Sundays and weekdays</strong>,
              when the road is calmer, the river is quieter, and the whole vibe is more "Hill
              Country" and less "spring break."
            </p>

            <h2 className="font-heading text-2xl font-bold text-charcoal mt-10 mb-4">
              Want something even more laid-back? Try Son's River Ranch.
            </h2>
            <p>
              If you're traveling with little kids — or you just want the most low-key, controlled
              tubing experience we offer — our sister property{" "}
              <a
                href="https://sonsriverranch.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal underline font-semibold"
              >
                Son's River Ranch
              </a>{" "}
              is a fantastic alternative near New Braunfels. It's a more contained, family-first
              setup that's hard to beat for first-time floaters and families with young kids.
              Different property, same family, same care.
            </p>

            <h2 className="font-heading text-2xl font-bold text-charcoal mt-10 mb-4">
              Come float the best stretch with us
            </h2>
            <p>
              Reserve a riverfront cabana, grab a burger from next door, and let the Guadalupe do
              the rest. We'll save you a spot on the best stretch of river near New Braunfels.
            </p>

            <div className="mt-8">
              <Link to="/book-now/sons-guadalupe">
                <Button variant="hero" size="lg" className="w-full sm:w-auto">
                  Book your riverfront cabana
                </Button>
              </Link>
            </div>
          </div>
        </article>

        <RelatedReads posts={related} />
      </main>

      <Footer />
    </div>
  );
};

export default BlogBestStretchGuadalupe;
